import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Icon } from 'components/ui/icon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { IFilterConfig, IFilterConfigNumber } from 'components/filter/types';
import React, { Dispatch, SetStateAction, useEffect } from 'react';

interface IProps {
  item: IFilterConfig;
  setValue: (arg1: string, arg2: number, arg3: string) => void;
  setSelected: (arg1: string, arg2: boolean) => void;
  setShowAmountError: Dispatch<SetStateAction<boolean>>;
  amountError: boolean;
  setSubmitExport: Dispatch<SetStateAction<boolean>>;
  submitExport: boolean;
}

const toId = (...parts: string[]) =>
  parts.join('-').toLowerCase().replace(/\s+/g, '-');

const NumberExport = ({
  item,
  setValue,
  setSelected,
  setShowAmountError,
  amountError,
  setSubmitExport,
  submitExport,
}: IProps): JSX.Element => {
  const { key, label, data, selected } = item;
  const { values } = data as IFilterConfigNumber;
  const condition = values[0];
  const firstNumber = values[1];
  const secondNumber = values[2];
  const subLabel = 'Filter by amount (Gross)';
  const amountId = toId(key, label);

  useEffect(() => {
    if (selected && submitExport) {
      if (condition !== 'between' && !values[1]) {
        setShowAmountError(true);
        setSubmitExport(false);
      } else if (condition === 'between' && (!values[1] || !values[2])) {
        setShowAmountError(true);
        setSubmitExport(false);
      } else {
        setShowAmountError(false);
        setSubmitExport(false);
      }
    }
  }, [values[1], values[2]]);

  const setCondition = (value: string) => {
    if (value !== 'between') {
      setValue(key, 0, value);
      setValue(key, 2, '');
    } else {
      setValue(key, 0, 'between');
    }
  };

  return (
    <>
      <div className="text-sm font-medium leading-6 text-gray-blue-80">
        {label}
      </div>
      <div className="flex items-center gap-2.5 pb-2 pt-2">
        <Checkbox
          id={amountId}
          data-testid="amount-check"
          checked={selected}
          onCheckedChange={(checked) => setSelected(key, checked === true)}
        />
        <label
          htmlFor={amountId}
          className="body-subtext-regular cursor-pointer text-gray-blue-80"
        >
          {subLabel}
        </label>
      </div>
      {selected && (
        <div className="mb-2 flex flex-col gap-2">
          <Select value={condition || 'equal'} onValueChange={setCondition}>
            <SelectTrigger data-testid="select-box" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="equal">equal</SelectItem>
              <SelectItem value="between">between</SelectItem>
              <SelectItem value="greater than">greater than</SelectItem>
              <SelectItem value="less than">less than</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Input
              data-testid="amount-field"
              type="number"
              placeholder="$0.00"
              className={amountError && !values[1] ? 'border-red-600' : ''}
              defaultValue={firstNumber}
              onChange={(e) => {
                setValue(key, 0, values[0]);
                setValue(key, 1, e.target.value);
              }}
            />

            {condition === 'between' && (
              <>
                <span className="text-gray-blue-50">–</span>
                <Input
                  data-testid="second"
                  type="number"
                  placeholder="$0.00"
                  className={amountError && !values[2] ? 'border-red-600' : ''}
                  defaultValue={secondNumber}
                  onChange={(e) => {
                    setValue(key, 0, values[0]);
                    setValue(key, 2, e.target.value);
                  }}
                />
              </>
            )}
          </div>

          {amountError && (
            <p
              className="mt-1 flex items-center text-sm text-red-600"
              data-testid="amount-error"
            >
              <Icon name="info" size={16} className="pr-2" />
              Please provide the amount to filter your results or uncheck the
              amount box
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default NumberExport;
